import {useEffect, useState} from 'react';
import {PERSONAS} from '../../config/personas';
import {ACCOUNT_ID} from '../../api/account';
import {cancelSubscription, removeAttachedCard} from '../../api/payment';
import {showError} from '../../utils/notify';

export default function MyContentPage({
                                          history,
                                          credits,
                                          subscription,
                                          plans,
                                          linkedTelegramId,
                                          linkedMaxId,
                                          onPlay,
                                          onPay,
                                          fetchAccountStatus,
                                          onGenerateClick,
                                          onBuyCredits,
                                          onRegenerate,
                                      }) {
    const [cancelling, setCancelling] = useState(false);
    const [removingCard, setRemovingCard] = useState(false);
    const handleCancelSubscription = async () => {
        if (!confirm('Отменить подписку? Доступ сохранится до конца оплаченного периода.')) return;
        setCancelling(true);
        try {
            await cancelSubscription({accountId: ACCOUNT_ID});
            await fetchAccountStatus?.();
        } catch (e) {
            console.error(e);
            showError('Не удалось отменить подписку. Попробуйте ещё раз.');
        } finally {
            setCancelling(false);
        }
    };
    const handleRemoveCard = async () => {
        if (!confirm('Удалить привязанную карту? Автопродление отключится, доступ сохранится до конца оплаченного периода.')) return;
        setRemovingCard(true);
        try {
            await removeAttachedCard({accountId: ACCOUNT_ID});
            await fetchAccountStatus?.();
        } catch (e) {
            console.error(e);
            showError('Не удалось удалить карту. Попробуйте ещё раз.');
        } finally {
            setRemovingCard(false);
        }
    };
    useEffect(() => {
        fetchAccountStatus();
        const interval = setInterval(fetchAccountStatus, 10000);
        return () => clearInterval(interval);
    }, []);

    const getStatusLabel = (item) => {
        if (!item.paid && item.status === 'pending') return 'Ожидает оплаты';
        const statusLabels = {
            pending: 'В очереди',
            processing: 'Генерация...',
            completed: 'Готово',
            failed: 'Ошибка (кредит возвращён)'
        };
        return statusLabels[item.status] || item.status;
    };

    const getStatusClass = (item) => {
        if (!item.paid && item.status === 'pending') return 'bg-pink-100 text-pink-700';
        if (item.status === 'completed') return 'bg-green-100 text-green-700';
        if (item.status === 'processing' || item.status === 'pending') return 'bg-blue-100 text-blue-700';
        return 'bg-red-100 text-red-700';
    };

    const handleShare = async (videoUrl, celebName) => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Видеопоздравление от ' + celebName,
                    text: 'Посмотри мое видеопоздравление от ИИ-звезды!',
                    url: videoUrl
                });
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Share failed:', error);
                }
            }
        }
    };

    // Sort by date (newest first)
    const items = [...history].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const subscriptionPlan = subscription?.plan_id
        ? (plans?.subscriptions || []).find(p => p.id === subscription.plan_id)
        : null;
    // Never surface the raw plan id (e.g. "sub_monthly_lite_video") — fall back to a neutral label.
    const subscriptionPlanLabel = subscriptionPlan?.name || 'Подписка';
    // Billing cadence comes straight from the plan's DB billing_period.
    const PERIOD_ADJECTIVES = {weekly: 'еженедельно', monthly: 'ежемесячно'};
    const subscriptionCadenceLabel = PERIOD_ADJECTIVES[subscriptionPlan?.billingPeriod] || '';

    const periodEndLabel = subscription?.current_period_end
        ? new Date(subscription.current_period_end).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        })
        : null;

    return (
        <div className="max-w-[1110px] mx-auto px-4 py-10">
            {/* Subscription Panel */}
            {subscription && (
                <div
                    className="bg-white border border-brand-dark/10 rounded-[20px] p-5 mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                        <p className="text-sm font-medium text-brand-dark/60">Ваша подписка</p>
                        <p className="text-lg font-bold text-brand-dark">
                            {subscriptionPlanLabel}
                            {subscriptionCadenceLabel && (
                                <span className="text-brand-dark/50 font-medium"> · {subscriptionCadenceLabel}</span>
                            )}
                        </p>
                        {periodEndLabel && (
                            <p className="text-sm text-brand-dark/60">
                                {subscription.cancel_at_period_end
                                    ? `Доступ до ${periodEndLabel}, автопродление отключено`
                                    : `Следующее списание: ${periodEndLabel}`}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:flex-wrap">
                        {!subscription.cancel_at_period_end && subscription.status === 'active' && (
                            <button
                                onClick={handleCancelSubscription}
                                disabled={cancelling}
                                className="px-5 py-2.5 bg-brand-gray text-brand-dark text-sm font-semibold rounded-xl hover:bg-gray-200 transition disabled:opacity-50"
                            >
                                {cancelling ? 'Отменяем...' : 'Отменить подписку'}
                            </button>
                        )}
                        {subscription.has_payment_method && (
                            <button
                                onClick={handleRemoveCard}
                                disabled={removingCard}
                                className="px-5 py-2.5 bg-brand-gray text-brand-dark text-sm font-semibold rounded-xl hover:bg-gray-200 transition disabled:opacity-50"
                            >
                                {removingCard ? 'Удаляем...' : 'Удалить карту'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Credits Banner */}
            <div
                className="bg-gradient-to-r from-brand-red/10 via-brand-pink/10 to-brand-orange/10 rounded-[20px] p-5 mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <p className="text-lg font-medium text-brand-dark">
                        Баланс: <span className="text-2xl font-bold text-brand-red">{credits}</span> видео
                    </p>
                    <p className="text-sm text-brand-dark/60">
                        {credits === 0
                            ? subscription
                                ? 'Дополнительные генерации скоро будут доступны при продлении'
                                : 'Оформите подписку или купите пакет видео'
                            : 'Доступно для генерации'
                        }
                    </p>
                </div>
                <button
                    onClick={onBuyCredits}
                    className="px-6 py-3 bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-semibold rounded-xl hover:opacity-90 transition"
                >
                    {subscription ? 'Купить ещё' : 'Выбрать тариф'}
                </button>
            </div>

            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8">
                <h1 className="text-[28px] md:text-[42px] font-bold text-brand-dark leading-[1.1] tracking-[-0.42px]">
                    Мои заказы
                </h1>
                <div className="flex gap-2">
                    <button
                        onClick={onGenerateClick}
                        className="px-6 py-3 bg-brand-gray text-brand-dark text-sm font-semibold rounded-xl hover:bg-gray-200 transition"
                    >
                        Создать видео
                    </button>
                </div>
            </div>

            {/* List */}
            {items.length === 0 ? (
                <div className="bg-brand-gray rounded-[20px] md:rounded-[30px] p-10 text-center">
                    <h3 className="text-2xl font-bold text-brand-dark mb-2">Тут могли бы быть ваши заказы</h3>
                    <p className="text-lg font-medium text-brand-dark/60">
                        но вы еще ничего не создали
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {items.map(item => {
                        const persona = PERSONAS.find(p => p.id === item.celeb_id);
                        return (
                            <div key={`v-${item.id}`}
                                 className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4 p-5 bg-brand-gray rounded-[20px] hover:bg-gray-100 transition">
                                <img
                                    src={persona?.image || ''}
                                    alt={persona?.name || item.celeb_id}
                                    className="w-full h-40 md:w-16 md:h-16 rounded-xl object-cover flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="text-xs font-semibold px-2 py-0.5 rounded-md bg-brand-dark/10 text-brand-dark/60">Видео</span>
                                        <span
                                            className="font-bold text-lg text-brand-dark">{persona?.name || item.celeb_id}</span>
                                    </div>
                                    <div className="text-sm text-brand-dark/60 truncate">{item.wish_text}</div>
                                    <div className="text-xs text-brand-dark/40 mt-1">
                                        {new Date(item.created_at).toLocaleString('ru-RU')}
                                    </div>
                                </div>
                                <div className="flex-shrink-0">
                                    <span
                                        className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${getStatusClass(item)}`}>
                                        {getStatusLabel(item)}
                                    </span>
                                </div>
                                <div className="flex gap-2 flex-shrink-0">
                                    {item.status === 'completed' && item.video_url && (
                                        <>
                                            <button
                                                onClick={() => onPlay(item.video_url)}
                                                className="flex-1 md:flex-none px-4 md:px-5 py-3 bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-semibold rounded-xl hover:opacity-90 transition"
                                            >
                                                Смотреть
                                            </button>
                                            <a
                                                href={item.video_url}
                                                download
                                                className="flex-1 md:flex-none px-4 md:px-5 py-3 bg-white text-brand-dark text-sm font-semibold rounded-xl hover:bg-gray-50 transition text-center"
                                            >
                                                Скачать
                                            </a>
                                            {typeof navigator !== 'undefined' && navigator.share && (
                                                <button
                                                    onClick={() => handleShare(item.video_url, persona?.name || item.celeb_id)}
                                                    className="md:hidden px-4 py-3 bg-white text-brand-dark text-sm font-semibold rounded-xl hover:bg-gray-50 transition"
                                                >
                                                    Поделиться
                                                </button>
                                            )}
                                        </>
                                    )}
                                    {!item.paid && item.status === 'pending' && (
                                        <button
                                            onClick={() => onPay(item)}
                                            className="flex-1 md:flex-none px-4 md:px-5 py-3 bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-semibold rounded-xl hover:opacity-90 transition"
                                        >
                                            Оплатить
                                        </button>
                                    )}
                                    {item.paid && (item.status === 'pending' || item.status === 'processing') && (
                                        <div
                                            className="w-8 h-8 border-3 border-gray-200 border-t-brand-red rounded-full animate-spin"></div>
                                    )}
                                    {item.status === 'failed' && (
                                        <button
                                            onClick={() => onRegenerate(item)}
                                            className="flex-1 md:flex-none px-4 md:px-5 py-3 bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-semibold rounded-xl hover:opacity-90 transition"
                                        >
                                            Повторить бесплатно
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
